import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { type UIMessage } from 'ai';
import { useEffect, useMemo, useRef } from 'react';

import { useAnalytics } from '~/analytics';
import Feedback from '~/components/feedback/Feedback';
import {
  ChatAttributionContext,
  RenderContentBlock,
} from '~/components/intelligence/ChatComponents';
import { ChatMarkdown } from '~/components/intelligence/ChatMarkdown';
import { OttoAvatar } from '~/components/otto/OttoAvatar';
import { useOtto } from '~/components/otto/OttoProvider';
import { SILENT_TOOLS, TOOL_ADAPTERS } from '~/components/otto/toolAdapters';
import { SURFACE_RADIUS } from '~/theme';

const GENUI_FEEDBACK_REASONS = [
  { key: 'inaccurate_data', label: 'Inaccurate data' },
  { key: 'wrong_format', label: 'Wrong chart/format' },
  { key: 'missed_question', label: 'Missed the question' },
  { key: 'hallucinated', label: 'Hallucinated' },
  { key: 'too_verbose', label: 'Too verbose' },
  { key: 'slow', label: 'Slow' },
] as const;

type ChatErrorWire = { code: string; message: string };

const GENERIC_CHAT_ERROR: ChatErrorWire = { code: 'unknown', message: 'Something went wrong.' };

// The terminal-stream path falls back here for a browser TypeError on
// fetch AND for a raw server body (the 403 `{"error": "GenUI chat is not
// enabled in this environment."}` was rendered verbatim to a customer),
// so the copy has to read correctly for both. The distinct `code` keeps
// the two apart in the caption and in analytics.
const TRANSPORT_CHAT_ERROR: ChatErrorWire = {
  code: 'transport_error',
  message: 'Something went wrong. Try again.',
};

function parseChatError(
  errorText?: string,
  fallback: ChatErrorWire = GENERIC_CHAT_ERROR,
): ChatErrorWire {
  if (!errorText) return fallback;
  try {
    const parsed: unknown = JSON.parse(errorText);
    if (parsed && typeof parsed === 'object') {
      const { code, message } = parsed as Record<string, unknown>;
      if (
        typeof code === 'string' &&
        code.trim() !== '' &&
        typeof message === 'string' &&
        message !== ''
      ) {
        return { code, message };
      }
    }
  } catch {
    /* not JSON */
  }
  // Never render an unrecognised payload verbatim. Reached by three real
  // shapes: a session persisted to localStorage before this deploy still
  // replaying the old bare `unknown tool: <name>` string; a browser
  // TypeError on fetch; and a raw DRF body — `{"error": "GenUI chat is
  // not enabled in this environment."}` reached a customer's screen this
  // way. Well-formed JSON with the wrong keys lands here too, which is
  // why this sits after the parse rather than only in the catch.
  return fallback;
}

function TextPart({ text }: { text: string }) {
  return <ChatMarkdown>{text}</ChatMarkdown>;
}

function ToolErrorPart({ error }: { error: ChatErrorWire }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        my: 1,
        px: 1.25,
        py: 1,
        // Reads as an inline alert, and alerts are cut at the panel radius.
        borderRadius: `${SURFACE_RADIUS}px`,
        background: alpha(theme.palette.error.main, 0.06),
        border: `1px solid ${alpha(theme.palette.error.main, 0.18)}`,
      }}
    >
      <ErrorOutlineRoundedIcon sx={{ fontSize: 16, mt: '2px', color: theme.palette.error.main }} />
      <Box>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: theme.palette.error.dark, display: 'block' }}
        >
          {error.message}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
          ({error.code})
        </Typography>
      </Box>
    </Box>
  );
}

type ToolPartShape = {
  type: string;
  toolCallId?: string;
  state?: string;
  output?: unknown;
  errorText?: string;
};

/** Did a later call of this same tool succeed within the same message?
 *
 * Six of the nine silent tools raise coaching the model can act on ("look
 * the id up with find_properties, then retry"), and the backend guidance
 * tells it to retry silently rather than bother the user. The retry is a
 * new toolCallId, so without this the intended happy path — name →
 * coaching → retry → correct answer — leaves a permanent red box sitting
 * above a correct answer.
 */
function laterCallSucceeded(parts: ToolPartShape[], index: number, toolName: string) {
  return parts
    .slice(index + 1)
    .some((p) => p.type === `tool-${toolName}` && p.state === 'output-available');
}

function ToolPart({
  part,
  parts,
  index,
}: {
  part: ToolPartShape;
  parts: ToolPartShape[];
  index: number;
}) {
  if (!part.type.startsWith('tool-')) return null;
  const toolName = part.type.slice('tool-'.length);
  // Silence is about SUCCESSFUL output: an id-lookup like find_properties
  // has no card to render, so a box would be noise. A failure is not that
  // — it means the answer the user is about to read rests on data that
  // never arrived. This check used to run first, so during AMP-665 a
  // failing find_properties showed no indicator at all and the model's
  // false "I can't find that property" was the user's only signal.
  //
  // The recovered case is the exception: suppressing it is safe because
  // the indicator is no longer the only signal — the model's narration is.
  // An unrecovered failure still gets narrated, so biasing toward less
  // noise here cannot recreate AMP-665, where silence was total.
  if (
    SILENT_TOOLS.has(toolName) &&
    (part.state !== 'output-error' || laterCallSucceeded(parts, index, toolName))
  ) {
    return null;
  }

  if (part.state === 'output-available') {
    const adapter = TOOL_ADAPTERS[toolName];
    const block = adapter ? adapter(part.output) : null;
    if (!block) return null;
    return (
      <Box sx={{ my: 1 }}>
        <RenderContentBlock block={block} />
      </Box>
    );
  }
  if (part.state === 'output-error') {
    return <ToolErrorPart error={parseChatError(part.errorText)} />;
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, my: 0.5, opacity: 0.7 }}>
      <CircularProgress size={10} thickness={5} />
      <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
        Calling {toolName}…
      </Typography>
    </Box>
  );
}

function UserBubble({ message }: { message: UIMessage }) {
  const theme = useTheme();
  const text = message.parts
    .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
    .map((p) => p.text)
    .join('\n');
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2.5, px: 1 }}>
      <Box
        sx={{
          maxWidth: '75%',
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, #3D7DD4)`,
          color: '#fff',
          borderRadius: '18px 18px 4px 18px',
          px: 2.5,
          py: 1.5,
        }}
      >
        <Typography variant="body2" sx={{ lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
          {text}
        </Typography>
      </Box>
    </Box>
  );
}

function AssistantBubble({ message, isStreaming }: { message: UIMessage; isStreaming: boolean }) {
  const { track } = useAnalytics();
  const runIdPart = message.parts.find((p) => (p as { type?: string }).type === 'data-run-id') as
    | { data?: { runId?: string } }
    | undefined;
  const runId = runIdPart?.data?.runId ?? null;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        mb: 2.5,
        px: 1,
        alignItems: 'flex-start',
      }}
    >
      <OttoAvatar size={30} loading={isStreaming} sx={{ mr: 1.5, mt: 0.5 }} />
      <Box
        sx={{
          flex: 1,
          maxWidth: 'min(95%, 720px)',
          background: '#fff',
          color: 'text.primary',
          borderRadius: '18px 18px 18px 4px',
          border: `1px solid ${alpha('#000', 0.06)}`,
          boxShadow: `0 1px 4px ${alpha('#000', 0.04)}`,
          px: 2.5,
          py: 2,
          overflow: 'hidden',
        }}
      >
        {message.parts.map((part, idx) => {
          if (part.type === 'text')
            return <TextPart key={`text-${message.id}-${idx}`} text={part.text} />;
          if (part.type.startsWith('tool-')) {
            const tp = part as { toolCallId?: string };
            return (
              <ToolPart
                key={tp.toolCallId ?? `tool-${idx}`}
                part={part}
                parts={message.parts as ToolPartShape[]}
                index={idx}
              />
            );
          }
          return null;
        })}
        {!isStreaming && runId ? (
          <Box sx={{ mt: 1.5, pt: 1, borderTop: `1px solid ${alpha('#000', 0.06)}` }}>
            <Feedback
              feedbackKey="genui-chat"
              runId={runId}
              reasonOptions={GENUI_FEEDBACK_REASONS}
              placeholder="Was this helpful?"
              onSubmitted={({ isPositive, reasons }) => {
                if (!runId) return;
                track('chat_feedback_submitted', {
                  run_id: runId,
                  rating: isPositive ? 'thumbs_up' : 'thumbs_down',
                  reasons,
                  feedback_key: 'genui-chat',
                });
              }}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

function TypingIndicator() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2.5, px: 1 }}>
      <OttoAvatar size={30} loading sx={{ mr: 1.5, mt: 0.5 }} />
      <Box
        sx={{
          background: '#fff',
          borderRadius: '18px 18px 18px 4px',
          border: `1px solid ${alpha('#000', 0.06)}`,
          px: 2.5,
          py: 1.75,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
          Analyzing your portfolio data…
        </Typography>
      </Box>
    </Box>
  );
}

export function OttoConversation({ sessionId }: { sessionId: string }) {
  const { state } = useOtto();
  const session = state.sessions[sessionId];
  const messages = useMemo<UIMessage[]>(() => session?.messages ?? [], [session?.messages]);
  const status = session?.status ?? 'ready';
  const errorMessage = session?.errorMessage ?? null;
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages, status]);

  return (
    <ChatAttributionContext.Provider value={{ sessionId, invoiceId: session?.invoiceId }}>
      <Box sx={{ maxWidth: 860, width: '100%', mx: 'auto', px: 2 }}>
        {messages.map((msg, i) => {
          const isLast = i === messages.length - 1;
          const isStreamingThis = isLast && status === 'streaming' && msg.role === 'assistant';
          return msg.role === 'user' ? (
            <UserBubble key={msg.id} message={msg} />
          ) : (
            <AssistantBubble key={msg.id} message={msg} isStreaming={isStreamingThis} />
          );
        })}
        {status === 'submitted' ? <TypingIndicator /> : null}
        {/* Terminal stream errors arrive as `errorText` too: the AI SDK
            wraps the `{"type":"error"}` event as `new Error(chunk.errorText)`
            and OttoChatRunner forwards `error.message` here unparsed. Left
            raw, the banner printed the literal JSON — `{"code":
            "agent_max_steps_exceeded", "message": "I had to stop..."}`.
            parseChatError also covers the non-JSON values this path
            receives (a browser TypeError on fetch failure, a DRF body),
            which fall to its `unknown` branch. */}
        {status === 'error' && errorMessage ? (
          <ToolErrorPart error={parseChatError(errorMessage, TRANSPORT_CHAT_ERROR)} />
        ) : null}
        <div ref={endRef} />
      </Box>
    </ChatAttributionContext.Provider>
  );
}
